import type {
  AuthResponse,
  Hunt,
  Egg,
  Player,
  Progress,
  ChatResponse,
  IdentifyResponse,
  GenerateResponse,
} from '../types'

const API_BASE = '/api'

function getToken(): string | null {
  return localStorage.getItem('auth_token')
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

export const api = {
  auth: {
    register(pin: string) {
      return request<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ pin }),
      })
    },
    login(familyId: string, pin: string) {
      return request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ family_id: familyId, pin }),
      })
    },
  },

  hunts: {
    list() {
      return request<{ hunts: (Hunt & { egg_count: number })[] }>('/hunts')
    },
    get(id: string) {
      return request<{ hunt: Hunt; eggs: Egg[]; players: Player[] }>(`/hunts/${id}`)
    },
    create(data: {
      name: string
      eggs: { description: string; difficulty: number; photo_key: string }[]
      config: { children_ages?: number[]; theme?: string }
    }) {
      return request<{ hunt_id: string }>('/hunts', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    update(id: string, data: Partial<Hunt>) {
      return request<{ success: boolean }>(`/hunts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    delete(id: string) {
      return request<{ success: boolean }>(`/hunts/${id}`, {
        method: 'DELETE',
      })
    },
    skipEgg(huntId: string, eggId: string) {
      return request<{ success: boolean }>(`/hunts/${huntId}/skip/${eggId}`, {
        method: 'POST',
      })
    },
    reset(huntId: string) {
      return request<{ success: boolean }>(`/hunts/${huntId}/reset`, {
        method: 'POST',
      })
    },
    createPlayer(huntId: string, name: string) {
      return request<{ player_id: string }>(`/hunts/${huntId}/players`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      })
    },
    getProgress(huntId: string, playerId: string) {
      return request<{ progress: Progress[] }>(`/hunts/${huntId}/progress/${playerId}`)
    },
    recordFound(huntId: string, data: {
      player_id: string
      egg_id: string
      hints_used: number
      photo_key?: string
      found_location?: string
      chat_log?: { role: string; content: string; timestamp: number }[]
    }) {
      return request<{ success: boolean }>(`/hunts/${huntId}/found`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
  },

  photos: {
    upload(photoBase64: string) {
      return request<{ key: string }>('/photos/upload', {
        method: 'POST',
        body: JSON.stringify({ photo_base64: photoBase64 }),
      })
    },
  },

  generate(huntId: string, eggs: { description: string; difficulty: number }[], config: { children_ages?: number[] }) {
    return request<{ success: boolean; story: GenerateResponse }>('/generate', {
      method: 'POST',
      body: JSON.stringify({ hunt_id: huntId, eggs, config }),
    })
  },

  chat(data: {
    hunt_id: string
    player_id: string
    egg_id: string
    message: string
    history: { role: string; content: string }[]
  }) {
    return request<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  identify(data: { hunt_id: string; player_id: string; photo_base64: string }) {
    return request<IdentifyResponse>('/identify', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  gemini: {
    getConfig() {
      return request<{ apiKey: string; model: string; wsUrl: string }>('/gemini/config')
    },
  },
}
